import React from "react";
import { Box, Divider, Grid, Skeleton, Typography } from "@mui/material";

interface SettingsRowProps {
  label: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
}

/** One company setting: label and optional description on the left, value on the right. */
const SettingsRow: React.FC<SettingsRowProps> = ({
  label,
  description,
  children,
}) => (
  <Grid container sx={{ padding: "var(--space-5) var(--space-6)" }} size={12}>
    <Grid sx={{ maxWidth: "500px", flexShrink: 0 }} size={{ xs: 12, sm: 6 }}>
      <Typography variant="h6">{label}</Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {description}
        </Typography>
      )}
    </Grid>

    <Grid
      sx={{
        paddingLeft: { xs: 0, sm: 4 },
        paddingTop: { xs: 1, sm: 0 },
      }}
      size={{ xs: 12, sm: 6 }}
    >
      {children}
    </Grid>
  </Grid>
);

/**
 * Placeholder rows for while a tab loads. Renders through SettingsRow itself so
 * the skeleton cannot drift from the geometry it stands in for.
 *
 * `rows` is one entry per row, true where that row carries a description. A row
 * without one is a line shorter, so assuming a description everywhere makes the
 * placeholder taller than what replaces it and the panel jumps.
 */
export const SettingsRowsSkeleton: React.FC<{
  rows: boolean[];
  label: string;
}> = ({ rows, label }) => (
  // A Skeleton announces nothing, so without this the tab reads as empty while
  // the query is in flight. The text it replaced at least said "Loading".
  <Box role="status" aria-busy="true" aria-label={label}>
    {rows.map((hasDescription, index) => (
      <React.Fragment key={index}>
        {index > 0 && <Divider />}
        <SettingsRow
          label={<Skeleton variant="text" width={170} />}
          description={
            hasDescription ? <Skeleton variant="text" width="85%" /> : undefined
          }
        >
          <Skeleton variant="text" width={220} />
        </SettingsRow>
      </React.Fragment>
    ))}
  </Box>
);

export default SettingsRow;
